import { useState, useEffect } from "react";
import type { AxiosInstance } from "axios";
import { fetchFwaasDetails } from "@shared/components/fwaas/request/request";
import { shapeFwaasData, type Fwaas } from "@shared/components/fwaas/FwaasData";
import type { HaMember, VmSpecs } from "kontrolEdge/components/Shared/VmSpecsCard";
import { dropCachedFetch, readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";
import { isTenantScopeForbidden, TENANT_OUT_OF_SCOPE_MESSAGE } from "@shared/helpers/tenantScope";
import type { Tunnels } from "./tunnels/tunnels";

export interface FwaasDetails {
    /** The service as the list shows it — the backend builds both with one function. */
    details: Fwaas;
    /** `null` when FortiManager is down, slow, or does not know the hosts (I5). */
    vm: VmSpecs | null;
    ha_members: HaMember[];
    /** `null` when FortiManager or the FortiGate cannot say (CTX-882) — never "no tunnels". */
    tunnels: Tunnels | null;
}

/**
 * Load one Virtual Firewall: GET /kontroledge/fwaas/:id?tenantId= (CTX-730).
 * The VM specs are read from FortiManager server-side; the endpoint never fails on
 * them, it answers with `vm: null` instead.
 */
export function useFwaasDetails(
    fwaasId: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
) {
    // The last answer for this service paints at once when the user comes back (a view
    // switch, Back); the request below still runs and replaces it.
    const cacheKey =
        fwaasId && tenantId != null
            ? `kontroledge/fwaas/${fwaasId}?tenantId=${tenantId}`
            : null;
    const [raw, setRaw] = useState<FwaasDetails | null>(
        () => readCachedFetch<FwaasDetails>(cacheKey) ?? null
    );
    const [loading, setLoading] = useState(() => !readCachedFetch(cacheKey));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!fwaasId || tenantId == null) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        // Another service on the same mounted page: its own cached answer or the
        // skeleton — never the previous service's data.
        const cached = readCachedFetch<FwaasDetails>(cacheKey);
        setRaw(cached ?? null);
        setLoading(!cached);
        setError(null);

        (async () => {
            try {
                const response = await fetchFwaasDetails(api, tenantId, fwaasId);
                if (cancelled) return;
                const body = response.data;
                if (!body?.success || !body.data?.details) {
                    throw new Error(body?.message ?? "Failed to load Virtual Firewall");
                }
                const loaded: FwaasDetails = {
                    details: shapeFwaasData([body.data.details])[0],
                    vm: body.data.vm ?? null,
                    ha_members: Array.isArray(body.data.ha_members) ? body.data.ha_members : [],
                    tunnels: body.data.tunnels ?? null,
                };
                writeCachedFetch(cacheKey, loaded);
                setRaw(loaded);
                setError(null);
            } catch (e) {
                // A tenant outside the caller's scope (CTX-866): forget what was cached for
                // it and show the refusal, not the firewall's last known state.
                if (isTenantScopeForbidden(e)) {
                    dropCachedFetch(cacheKey);
                    if (!cancelled) {
                        setRaw(null);
                        setError(TENANT_OUT_OF_SCOPE_MESSAGE);
                    }
                    return;
                }
                if (cancelled) return;
                const status = (e as { response?: { status?: number } })?.response?.status;
                // 404 → not found (empty state), even over a cached answer. The backend
                // also answers 404 for another tenant's id, so ids cannot be probed.
                if (status === 404) {
                    setRaw(null);
                    setError(null);
                    return;
                }
                // Any other failure: a cached answer on screen stays there; with nothing
                // to show → error state with retry.
                if (!cached) {
                    setRaw(null);
                    setError("We couldn't load this Virtual Firewall. Please try again.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [fwaasId, tenantId, api, refresh, cacheKey]);

    return { raw, loading, error };
}
